import { ApiService } from './api';

// Enhanced interfaces for authentication
export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  token_type: string;
  expires_in: number;
  user: User;
  session_id: string;
  requires_mfa?: boolean;
}

export interface RegisterRequest {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  password_confirmation: string;
  company: string;
  website?: string;
  industry?: string;
  phone?: string;
}

export interface User {
  id: number;
  name: string;
  email: string;
  role: 'admin' | 'client' | 'project_manager' | 'project_manager_lead' | 'developer' | 'developer_lead';
  is_active: boolean;
  account_status: 'active' | 'inactive' | 'suspended';
  mfa_enabled: boolean;
  last_login_at?: string;
  phone?: string;
  created_at: string;
  updated_at: string;
}

export interface ProfileResponse {
  user: User;
  permissions: string[];
  active_sessions: number;
}

export interface ChangePasswordRequest {
  current_password: string;
  password: string;
  password_confirmation: string;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  email: string;
  token: string;
  password: string;
  password_confirmation: string;
}

export interface VerifyResetTokenRequest {
  email: string;
  token: string;
}

export interface EmailVerificationRequest {
  email: string;
  token: string;
}

export interface ResendVerificationRequest {
  email: string;
}

// Storage keys
const STORAGE_KEYS = {
  TOKEN: 'auth_token',
  USER: 'user_data',
  SESSION_ID: 'session_id',
  TOKEN_EXPIRY: 'token_expiry',
  PERMISSIONS: 'user_permissions'
} as const;

/**
 * Enhanced Authentication Service
 * Handles all authentication-related API calls and token management
 */
export class EnhancedAuthService {
  private static refreshPromise: Promise<LoginResponse> | null = null;

  /**
   * Login user with email and password
   */
  static async login(request: LoginRequest): Promise<LoginResponse> {
    try {
      console.log('AuthService: Sending login request to API');
      const response = await ApiService.post<{
        success: boolean;
        message: string;
        data: LoginResponse;
      }>('/auth/login', request);
      
      console.log('AuthService: Received response', JSON.stringify({
        success: response.success,
        hasToken: !!response.data?.token,
        hasUser: !!response.data?.user,
        message: response.message
      }, null, 2));
      
      if (!response.success) {
        console.error('AuthService: Login response indicated failure:', response.message);
        throw new Error(response.message || 'Login failed');
      }
      
      // Store authentication data
      this.storeAuthData(response.data);
      console.log('AuthService: Auth data stored successfully');
      
      return response.data;
    } catch (error: any) {
      const errorInfo = {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
        hasResponse: !!error.response,
        requestId: error.requestId,
        url: error.config?.url,
        method: error.config?.method
      };
      
      console.error('AuthService: Login error caught', JSON.stringify(errorInfo, null, 2));
      
      // Handle specific error cases
      if (error.response?.status === 422) {
        const errorMessage = error.response.data?.message || 'Invalid credentials';
        throw new Error(errorMessage);
      }
      
      if (error.response?.status === 423) {
        throw new Error('Account is temporarily locked. Please try again later.');
      }
      
      // If it's already an Error with a message, rethrow it
      if (error instanceof Error) {
        throw error;
      }
      
      throw new Error(error.message || 'Login failed. Please try again.');
    }
  }

  /**
   * Register new user
   */
  static async register(request: RegisterRequest): Promise<LoginResponse> {
    try {
      const response = await ApiService.post<{
        success: boolean;
        message: string;
        data: LoginResponse;
      }>('/auth/register', request);
      
      if (!response.success) {
        throw new Error(response.message || 'Registration failed');
      }
      
      // Store authentication data
      this.storeAuthData(response.data);
      
      return response.data;
    } catch (error: any) {
      // Handle validation errors
      if (error.response?.status === 422) {
        const errors = error.response.data?.errors;
        if (errors) {
          const firstError = Object.values(errors)[0] as string[];
          throw new Error(firstError[0] || 'Validation failed');
        }
      }
      
      throw new Error(error.message || 'Registration failed. Please try again.');
    }
  }

  /**
   * Logout user and clear session
   */
  static async logout(): Promise<void> {
    try {
      await ApiService.post<{
        success: boolean;
        message: string;
      }>('/auth/logout');
    } catch (error) {
      console.warn('Logout API call failed:', error);
    } finally {
      // Always clear local storage
      this.clearAuthData();
    }
  }

  /**
   * Refresh authentication token
   */
  static async refreshToken(): Promise<LoginResponse> {
    // Prevent multiple simultaneous refresh requests
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this.performTokenRefresh();
    
    try {
      const result = await this.refreshPromise;
      return result;
    } finally {
      this.refreshPromise = null;
    }
  }

  private static async performTokenRefresh(): Promise<LoginResponse> {
    try {
      const response = await ApiService.post<{
        success: boolean;
        message: string;
        data: {
          token: string;
          token_type: string;
          expires_in: number;
        };
      }>('/auth/refresh');
      
      if (!response.success) {
        throw new Error(response.message || 'Token refresh failed');
      }
      
      // Update stored token
      localStorage.setItem(STORAGE_KEYS.TOKEN, response.data.token);
      this.setTokenExpiry(response.data.expires_in);
      
      // Return refreshed data with existing user info
      const user = this.getUser();
      return {
        ...response.data,
        user: user!,
        session_id: this.getSessionId() || ''
      };
    } catch (error) {
      // Clear auth data on refresh failure
      this.clearAuthData();
      throw error;
    }
  }

  /**
   * Get user profile and permissions
   */
  static async getProfile(): Promise<ProfileResponse> {
    try {
      const response = await ApiService.get<{
        success: boolean;
        message: string;
        data: ProfileResponse;
      }>('/auth/profile');
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to get profile');
      }
      
      // Update stored user data and permissions
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(response.data.user));
      localStorage.setItem(STORAGE_KEYS.PERMISSIONS, JSON.stringify(response.data.permissions));
      
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 401) {
        this.clearAuthData();
      }
      throw error;
    }
  }

  /**
   * Change user password
   */
  static async changePassword(request: ChangePasswordRequest): Promise<void> {
    try {
      const response = await ApiService.post<{
        success: boolean;
        message: string;
      }>('/auth/change-password', request);
      
      if (!response.success) {
        throw new Error(response.message || 'Password change failed');
      }
    } catch (error: any) {
      if (error.response?.status === 422) {
        const errorMessage = error.response.data?.message || 'Current password is incorrect';
        throw new Error(errorMessage);
      }
      
      throw new Error(error.message || 'Password change failed');
    }
  }

  /**
   * Request password reset
   */
  static async forgotPassword(request: ForgotPasswordRequest): Promise<{ success: boolean; message: string; token?: string }> {
    try {
      const response = await ApiService.post<{
        success: boolean;
        message: string;
        token?: string;
      }>('/auth/password/forgot', request);
      
      return response;
    } catch (error: any) {
      if (error.response?.status === 422) {
        const errorMessage = error.response.data?.message || 'Invalid email address';
        throw new Error(errorMessage);
      }
      
      throw new Error(error.message || 'Failed to request password reset');
    }
  }

  /**
   * Verify password reset token
   */
  static async verifyResetToken(request: VerifyResetTokenRequest): Promise<{ success: boolean; message: string }> {
    try {
      const response = await ApiService.post<{
        success: boolean;
        message: string;
      }>('/auth/password/verify-token', request);
      
      return response;
    } catch (error: any) {
      if (error.response?.status === 422) {
        const errorMessage = error.response.data?.message || 'Invalid or expired reset token';
        throw new Error(errorMessage);
      }
      
      throw new Error(error.message || 'Failed to verify reset token');
    }
  }

  /**
   * Reset password with token
   */
  static async resetPassword(request: ResetPasswordRequest): Promise<{ success: boolean; message: string }> {
    try {
      const response = await ApiService.post<{
        success: boolean;
        message: string;
      }>('/auth/password/reset', request);
      
      return response;
    } catch (error: any) {
      if (error.response?.status === 422) {
        const errors = error.response.data?.errors;
        if (errors) {
          const firstError = Object.values(errors)[0] as string[];
          throw new Error(firstError[0] || 'Validation failed');
        }
        const errorMessage = error.response.data?.message || 'Invalid or expired reset token';
        throw new Error(errorMessage);
      }
      
      throw new Error(error.message || 'Failed to reset password');
    }
  }

  /**
   * Verify email address
   */
  static async verifyEmail(request: EmailVerificationRequest): Promise<{ success: boolean; message: string }> {
    try {
      const response = await ApiService.post<{
        success: boolean;
        message: string;
      }>('/auth/email/verify', request);
      
      return response;
    } catch (error: any) {
      if (error.response?.status === 422) {
        const errorMessage = error.response.data?.message || 'Invalid or expired verification token';
        throw new Error(errorMessage);
      }
      
      throw new Error(error.message || 'Failed to verify email');
    }
  }

  /**
   * Resend email verification
   */
  static async resendVerification(request: ResendVerificationRequest): Promise<{ success: boolean; message: string; token?: string }> {
    try {
      const response = await ApiService.post<{
        success: boolean;
        message: string;
        token?: string;
      }>('/auth/email/resend', request);
      
      return response;
    } catch (error: any) {
      if (error.response?.status === 422) {
        const errorMessage = error.response.data?.message || 'Email is already verified or account does not exist';
        throw new Error(errorMessage);
      }
      
      throw new Error(error.message || 'Failed to resend verification email');
    }
  }

  /**
   * Check if user is authenticated
   */
  static isAuthenticated(): boolean {
    const token = this.getToken();
    const user = this.getUser();
    
    if (!token || !user) {
      return false;
    }
    
    // Check if token is expired
    const expiry = this.getTokenExpiry();
    if (expiry && Date.now() >= expiry) {
      this.clearAuthData();
      return false;
    }
    
    return true;
  }

  /**
   * Check if token needs refresh (within 5 minutes of expiry)
   */
  static shouldRefreshToken(): boolean {
    const expiry = this.getTokenExpiry();
    if (!expiry) return false;
    
    const fiveMinutes = 5 * 60 * 1000;
    return Date.now() >= (expiry - fiveMinutes);
  }

  // Storage management methods
  static getToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.TOKEN);
  }

  static getUser(): User | null {
    const userData = localStorage.getItem(STORAGE_KEYS.USER);
    return userData ? JSON.parse(userData) : null;
  }

  static getSessionId(): string | null {
    return localStorage.getItem(STORAGE_KEYS.SESSION_ID);
  }

  static getPermissions(): string[] {
    const permissions = localStorage.getItem(STORAGE_KEYS.PERMISSIONS);
    return permissions ? JSON.parse(permissions) : [];
  }

  private static getTokenExpiry(): number | null {
    const expiry = localStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRY);
    return expiry ? parseInt(expiry, 10) : null;
  }

  private static setTokenExpiry(expiresIn: number): void {
    const expiryTime = Date.now() + (expiresIn * 1000);
    localStorage.setItem(STORAGE_KEYS.TOKEN_EXPIRY, expiryTime.toString());
  }

  private static storeAuthData(data: LoginResponse): void {
    localStorage.setItem(STORAGE_KEYS.TOKEN, data.token);
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(data.user));
    localStorage.setItem(STORAGE_KEYS.SESSION_ID, data.session_id);
    this.setTokenExpiry(data.expires_in);
  }

  private static clearAuthData(): void {
    Object.values(STORAGE_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
  }
}

// Backward compatibility - re-export as AuthService
export { EnhancedAuthService as AuthService };
export default EnhancedAuthService;